import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Author, Category, PostSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { buttonStyles } from '@/lib/button'
import { getRequestOrigin } from '@/lib/request'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { AuthorCard } from '@/components/blog/author-card'
import { EmptyState } from '@/components/blog/empty-state'
import { FeaturedStory } from '@/components/blog/featured-story'
import { LoadError } from '@/components/blog/load-error'
import { PostCard } from '@/components/blog/post-card'
import { PostGrid } from '@/components/blog/post-grid'
import { SectionHeading } from '@/components/blog/section-heading'
import { TopicCard } from '@/components/blog/topic-card'

import {
    getAuthors,
    getCategories,
    getFeaturedPosts,
    getLatestPosts
} from '@/lib/blog'

interface HomeProps {
    lead: PostSummary | null
    picks: PostSummary[]
    latest: PostSummary[]
    categories: Category[]
    authors: Author[]
    url: string
    failed: boolean
}

const Home: FC<HomeProps> = ({
    lead,
    picks,
    latest,
    categories,
    authors,
    url,
    failed
}): ReactNode => {
    return (
        <Layout>
            <Seo url={url || undefined} ogImage={lead?.coverImage} />

            <section id='hero' className='border-b border-border'>
                <Container className='pb-12 pt-12 sm:pb-14 sm:pt-16 lg:pb-16 lg:pt-24'>
                    <div className='grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-12'>
                        <div className='animate-in fade-in slide-in-from-bottom-3 duration-700 motion-reduce:animate-none lg:col-span-8'>
                            <p className='text-xs font-semibold uppercase tracking-[0.18em] text-brand'>
                                An independent journal
                            </p>

                            <h1 className='mt-5 font-heading text-[3.1rem] font-medium leading-[0.98] tracking-[-0.02em] text-balance sm:text-7xl lg:text-[5.75rem]'>
                                Stories worth n <em className='italic text-brand'>slowing dow ssn</em> fordxd dd.
                            </h1>
                        </div>

                        <div className='animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-150 duration-700 motion-reduce:animate-none lg:col-span-4 lg:pb-2'>
                            <p className='text-lg leading-relaxed text-muted-foreground'>
                                Essays and practical guides on design, dd, food and culture. New stories eveddry week, from writers who take their time.
                            </p>

                            <div className='mt-7 flex flex-wrap gap-3'>
                                <Link href='/blog' className={buttonStyles('primary')}>
                                    ddd Browse all stories
                                    <ArrowRight className='size-4' aria-hidden='true' />
                                </Link>

                                <a href='#newsletter' className={buttonStyles('outline')}>
                                    Get ddd newsletter
                                </a>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {failed && (
                <Container className='py-16'>
                    <LoadError />
                </Container>
            )}

            {!failed && !lead && (
                <Container className='py-16'>
                    <EmptyState
                        title='The first story is on its way'
                        description='New essays and guides are being written right now. Join the newsletter to hear the moment they are published.'
                        action={{ label: 'Join the newsletter', href: '#newsletter', fullReload: true }}
                    />
                </Container>
            )}

            {lead && (
                <section id='featured' aria-labelledby='featured-heading'>
                    <Container className='py-12 sm:py-16'>
                        <h2 id='featured-heading' className='sr-only'>
                            Featured stories
                        </h2>

                        <div className='grid gap-14 lg:grid-cols-12 lg:gap-12'>
                            <div className={picks.length > 0 ? 'lg:col-span-8' : 'lg:col-span-10 lg:col-start-2'}>
                                <FeaturedStory post={lead} />
                            </div>

                            {picks.length > 0 && (
                                <aside
                                    aria-labelledby='picks-heading'
                                    className='border-t border-border pt-10 lg:col-span-4 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0'
                                >
                                    <h2 id='picks-heading' className='text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                        Editor's picks
                                    </h2>

                                    <div className='mt-6 grid gap-12 sm:grid-cols-2 lg:grid-cols-1'>
                                        {picks.map(post => (
                                            <PostCard key={post.id} post={post} showExcerpt={false} priority />
                                        ))}
                                    </div>
                                </aside>
                            )}
                        </div>
                    </Container>
                </section>
            )}

            {latest.length > 0 && (
                <section id='latest' aria-labelledby='latest-heading'>
                    <Container className='py-12 sm:py-16'>
                        <SectionHeading
                            id='latest-heading'
                            eyebrow='Fresh off the desk'
                            title='Latest stories'
                            action={{ label: 'View all stories', href: '/blog' }}
                        />

                        <PostGrid posts={latest} headingLevel='h3' className='mt-12' />
                    </Container>
                </section>
            )}

            {categories.length > 0 && (
                <section id='topics' aria-labelledby='topics-heading' className='mt-8 border-y border-border bg-secondary/70'>
                    <Container className='py-16 sm:py-20'>
                        <SectionHeading
                            id='topics-heading'
                            eyebrow='Explore'
                            title='Find your next read by topic'
                            action={{ label: 'All topics', href: '/topics' }}
                        />

                        <div className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
                            {categories.map(category => (
                                <TopicCard key={category.id} category={category} />
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            {authors.length > 0 && (
                <section id='writers' aria-labelledby='writers-heading'>
                    <Container className='pt-16 sm:pt-24'>
                        <div className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                            <div className='lg:col-span-4 lg:pr-6'>
                                <p className='text-xs font-semibold uppercase tracking-[0.18em] text-brand'>
                                    The writers
                                </p>

                                <h2 id='writers-heading' className='mt-3 font-heading text-3xl font-medium tracking-tight text-balance sm:text-[2.6rem] sm:leading-[1.1]'>
                                    Written by people who take their time.
                                </h2>

                                <p className='mt-4 text-base leading-relaxed text-muted-foreground'>
                                    Longhand is a small team of editors, cooks and travellers. We write about what we know firsthand, and we only publish what we would want to read ourselves.
                                </p>

                                <Link
                                    href='/about'
                                    className='group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-foreground transition-colors hover:text-brand'
                                >
                                    About the journal
                                    <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' aria-hidden='true' />
                                </Link>
                            </div>

                            <div className='grid gap-6 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3'>
                                {authors.slice(0, 3).map(author => (
                                    <AuthorCard key={author.id} author={author} />
                                ))}
                            </div>
                        </div>
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async ({ req, res }) => {
    const origin = getRequestOrigin(req)
    const url = origin ? `${origin}/` : ''

    try {
        const [featured, recent, categories, authors] = await Promise.all([
            getFeaturedPosts(3),
            getLatestPosts(12),
            getCategories(),
            getAuthors()
        ])

        const lead = featured[0] ?? recent[0] ?? null
        const seen = new Set<string>(lead ? [lead.id] : [])
        const picks: PostSummary[] = []

        for (const post of [...featured, ...recent]) {
            if (picks.length === 2) break
            if (seen.has(post.id)) continue

            seen.add(post.id)
            picks.push(post)
        }

        const latest = recent.filter(post => !seen.has(post.id)).slice(0, 6)

        return {
            props: { lead, picks, latest, categories, authors, url, failed: false }
        }
    }
    catch (error) {
        console.error('Could not load the home page:', error)
        res.statusCode = 503

        return {
            props: { lead: null, picks: [], latest: [], categories: [], authors: [], url, failed: true }
        }
    }
}

export default Home
